import Scene from "./Scene.tsx";
import {Sidebar} from "./Sidebar.tsx";
import {type Dispatch, type SetStateAction, useCallback, useEffect, useState} from "react";
import {Footer} from "./footer.tsx";

export type ValuesMap = Record<string, number | string>;
export type SidebarValues = Dispatch<SetStateAction<ValuesMap>>;

/**
 * The main application.
 * It consists of a sidebar, a 3D canvas and an imprint.
 *
 * The sidebar displays values of known keys.
 * The scene sets key-value pairs whenever it wants.
 * The footer satisfies compliance requirements.
 */
export default function App() {

    const [values, setValues] = useState<ValuesMap>({});
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);

    // Listen for orientation changes
    useEffect(() => {
        const handleResize = () => {
            setIsPortrait(window.innerHeight > window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleFullscreen = useCallback(() => {
        // Type for screen.orientation with optional lock/unlock methods
        const orientation = screen.orientation as ScreenOrientation & {
            lock?: (orientation: string) => Promise<void>;
            unlock?: () => void;
        };

        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().then(() => {
                setIsFullscreen(true);
                // Try to lock orientation to landscape
                if (orientation?.lock) {
                    orientation.lock('landscape').catch(() => {
                        // Orientation lock not supported or failed - that's ok
                    });
                }
            });
        } else {
            document.exitFullscreen().then(() => {
                setIsFullscreen(false);
                // Unlock orientation
                if (orientation?.unlock) {
                    orientation.unlock();
                }
            });
        }
    }, []);

    return (
        <div className="app-container">
            <div className="main-content">
                <Sidebar values={values} isFullscreen={isFullscreen} toggleFullscreen={toggleFullscreen}/>
                <Scene setValues={setValues}/>
            </div>
            {!isFullscreen && <Footer/>}
            {isFullscreen && isPortrait && (
                <div className="rotate-hint">
                    <span className="rotate-icon">📱</span>
                    <span>Bitte drehe dein Gerät ins Querformat</span>
                </div>
            )}
        </div>
    )
}
